import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, Typography, TextField, Button, Box, Alert, Link,
} from '@mui/material';
import { useAuth } from './AuthContext';

const READONLY_KEY = 'aussidex_readonly_dismissed';

/**
 * IDEX-style startup login: the app is read-only by default and this dialog
 * offers to unlock mission changes. "Use in Read-Only Mode" dismisses it for
 * the session.
 */
export default function LoginDialog() {
  const { user, initializing, login, register } = useAuth();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!initializing && !user && localStorage.getItem(READONLY_KEY) !== '1') {
      setOpen(true);
    }
  }, [initializing, user]);

  const close = () => setOpen(false);

  const dismissReadOnly = () => {
    localStorage.setItem(READONLY_KEY, '1');
    close();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const err = mode === 'login'
      ? await login(email, password)
      : await register(email, password, firstName, lastName);
    setBusy(false);
    if (err) {
      setError(err);
    } else {
      close();
    }
  };

  return (
    <Dialog open={open} onClose={dismissReadOnly} maxWidth="xs" fullWidth>
      <DialogContent sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {mode === 'login' ? 'Log In' : 'Create Account'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Aussidex is read-only by default. Log in below to enable mission changes.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {mode === 'register' && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required size="small" fullWidth />
              <TextField label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} required size="small" fullWidth />
            </Box>
          )}
          <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required size="small" autoFocus />
          <TextField label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required size="small" />
          <Button type="submit" variant="contained" disabled={busy} sx={{ mt: 1 }}>
            {mode === 'login' ? 'Log In' : 'Create Account'}
          </Button>
          <Button variant="outlined" color="inherit" onClick={dismissReadOnly}>
            Use Aussidex in Read-Only Mode
          </Button>
          <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
            {mode === 'login' ? (
              <>No account?{' '}
                <Link component="button" type="button" onClick={() => { setMode('register'); setError(null); }}>
                  Create one
                </Link>
              </>
            ) : (
              <>Already registered?{' '}
                <Link component="button" type="button" onClick={() => { setMode('login'); setError(null); }}>
                  Log in
                </Link>
              </>
            )}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
